/**
 * useMyEstate (B3) -- what this person owns.
 *
 * Matched on email, case-insensitively. `matched_on` is null when the
 * token carried no email, which is a different answer from owning
 * nothing and the view says so.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";

export interface OwnedService {
  id: string;
  code: string;
  name: string;
  classification: string | null;
  open_findings: number;
  owner_role: string | null;
}

export interface OwnedIBS {
  id: string;
  code: string;
  name: string;
  admission_status: string;
  mvb_flag: boolean;
  impact_tolerance_hours: number | null;
  open_findings: number;
  /** D4: what the owner has already written down. The panel's fields
   *  started empty on every visit, so a description saved last week
   *  read as one never written. */
  description?: string | null;
  channels?: string | null;
  /** Round 16 -- the register class; a CIF has no channel. */
  service_class?: "customer" | "state" | "internal";
  /** D5: what this IBS is recorded as depending on, so the owner can
   *  see what saved and take one back off. */
  supporting_services?: { id: string; name: string; source: string | null }[];
  /** Round 17 N9 -- the date it was admitted, where it was. */
  admitted_on?: string | null;
}

export interface MyEstate {
  matched_on: string | null;
  services: OwnedService[];
  ibses: OwnedIBS[];
}

export function useMyEstate(): UseQueryResult<MyEstate, ApiError> {
  return useQuery({
    queryKey: ["my-estate"],
    queryFn: () => api.get<MyEstate>("/v1/me/estate"),
    staleTime: 30_000,
  });
}
